(() => {
  'use strict';
  if (window.AvalonLivePricing) { window.AvalonLivePricing.init(); return; }
  const root = window.Shopify?.routes?.root || '/';
  const query = `mutation Quote($input:CartInput!){cartCreate(input:$input){cart{id cost{subtotalAmount{amount currencyCode}} discountAllocations{discountedAmount{amount currencyCode}} lines(first:100){nodes{quantity cost{totalAmount{amount currencyCode} subtotalAmount{amount currencyCode}} merchandise{... on ProductVariant{id}} discountAllocations{discountedAmount{amount currencyCode}}}}} userErrors{field message}}}`;
  const cache = new Map();
  // 27 Oct 2026. Shopify answers a price question only through a cart it makes and forgets (cartCreate). Asked from
  // the page, Shopify's own storefront tracking reads that cart as a shopper adding to cart: Shopify Analytics counts a
  // cart addition and Meta receives AddToCart (pixel and Conversions API) for every line of every price shown. So the
  // question is asked from a worker of its own, where the page's tracking does not listen, and without cookies, so the
  // cart belongs to no one. With no worker there is no live price (each block says the cart will show it); the question
  // is never asked from the page itself. Real additions to the cart (the buttons) still go through /cart/add.js and
  // are tracked as before.
  let quoteWorker = null, quoteSeq = 0;
  const quoteWaiting = new Map();
  const askShopify = body => new Promise((resolve, reject) => {
    try {
      if (!quoteWorker) {
        const source = "self.onmessage=async e=>{const{id,url,body}=e.data;try{const r=await fetch(url,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body,credentials:'omit'});self.postMessage({id,ok:r.ok,text:await r.text()});}catch(error){self.postMessage({id,ok:false,text:''});}};";
        quoteWorker = new Worker(URL.createObjectURL(new Blob([source], {type:'text/javascript'})));
        quoteWorker.onmessage = e => {
          const waiting = quoteWaiting.get(e.data && e.data.id);
          if (!waiting) return;
          quoteWaiting.delete(e.data.id); clearTimeout(waiting.timer); waiting.resolve(e.data);
        };
        quoteWorker.onerror = () => {
          quoteWaiting.forEach(waiting => { clearTimeout(waiting.timer); waiting.reject(new Error('Offer preview unavailable.')); });
          quoteWaiting.clear(); quoteWorker = null;
        };
      }
      const id = ++quoteSeq;
      const timer = setTimeout(() => { quoteWaiting.delete(id); reject(new Error('Offer preview unavailable.')); }, 12000);
      quoteWaiting.set(id, {resolve, reject, timer});
      quoteWorker.postMessage({id, url: new URL('/api/2026-07/graphql.json', location.origin).href, body});
    } catch (error) {
      reject(new Error('Offer preview unavailable.'));
    }
  });
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency}).format(amount);
  // Prices drawn the way Liquid draws them, for an element that hands over a sample (data-money-sample): 123456789
  // minor units that Liquid formatted in the visitor's currency. The symbol, separators and decimals are read from
  // it, and whole amounts lose their decimals, as money_without_trailing_zeros does. A sample that does not read
  // cleanly gives null, and the browser's own format above is used.
  const moneyLike = sample => {
    const plain = document.createElement('textarea');
    plain.innerHTML = String(sample || '').replace(/<[^>]*>/g, '');
    const parts = plain.value.trim().match(/^(\D*)(\d[\s\S]*\d)(\D*)$/);
    if (!parts) return null;
    const groups = parts[2].split(/\D+/);
    const seps = parts[2].match(/\D+/g) || [];
    const digits = groups.join('');
    let shape = null;
    if (digits === '123456789' && groups[groups.length-1].length === 2 && seps.length > 0) shape = {before:parts[1],after:parts[3],decimals:2,decimal:seps[seps.length-1],thousands:seps.length>1?seps[0]:''};
    else if (digits === '1234568' || digits === '1234567') shape = {before:parts[1],after:parts[3],decimals:0,decimal:'',thousands:seps.length>0?seps[0]:''};
    if (!shape) return null;
    return amount => {
      const cents = Math.round(Number(amount) * 100);
      const size = Math.abs(cents);
      const fixed = shape.decimals === 0 ? String(Math.round(size / 100)) : (size / 100).toFixed(size % 100 === 0 ? 0 : 2);
      const [int, frac] = fixed.split('.');
      return (cents < 0 ? '-' : '') + shape.before + int.replace(/\B(?=(\d{3})+(?!\d))/g, shape.thousands) + (frac ? shape.decimal + frac : '') + shape.after;
    };
  };
  // 27 Oct 2026. Rounded prices outside the dollar. Shopify rounds every single price it converts (Markets, price
  // rounding: up to a whole pound or dollar, to 100 yen or rupees, to 1,000 rupiah or won, to .95 for the euro). A
  // set is not one price: it is several, less a discount that Shopify converts to the cent, so its total reads
  // £127.38 or Rp 3.061.874,22. Shown on the page, such a total is rounded as Shopify rounds that currency's single
  // prices, and always up: the cart and checkout charge the exact total, never more than the page said. What is saved
  // or taken off is rounded down (to whole units for the euro), and an "about" figure to the nearest unit. Dollars stay
  // exact. Every answer carries its rule (q.rounding), read from the single prices in it: if those are not rounded
  // (rounding switched off for that market, or a price set by hand), the totals stay exact as well.
  const STEPS = {};
  'KRW BIF GNF STD SLL TZS UGX KHR IDR LAK MNT UZS VND PYG'.split(' ').forEach(c => { STEPS[c] = 1000; });
  'JPY DZD XOF XAF CVE KMF DJF KES MWK NGN RWF AMD BDT INR KZT KGS NPR PKR PHP LKR ALL ISK HUF MKD RSD CRC DOP JMD XPF VUV GYD'.split(' ').forEach(c => { STEPS[c] = 100; });
  // upTo(x, ceiling): x rounded up, but never above ceiling (the same items at their single prices, which is never
  // below what Shopify charges) unless x itself is: €70,95 + €90,95 stays €161,90 and is not taken to €161,95.
  const exact = Object.freeze({on:false, unit:0, up:x => x, upTo:x => x, down:x => x, near:x => x, fits:() => true});
  const rounding = (currency, units) => {
    // Dollars, and currencies with three decimals (cents would lose a digit), are always shown exactly.
    if (!currency || currency === 'USD' || /^(BHD|IQD|JOD|KWD|LYD|OMR|TND)$/.test(currency)) return exact;
    const step = currency === 'EUR' ? 0 : (STEPS[currency] || 1) * 100; // in cents; 0 is the euro's .95
    const unit = step || 100;
    const toCents = x => Math.round(Number(x) * 100);
    const fits = c => (step ? c % step === 0 : c % 100 === 95);
    const samples = (units || []).map(toCents).filter(c => c > 0);
    if (!samples.length || !samples.every(fits)) return exact;
    const up = x => { const c = toCents(x); if (!(c > 0)) return c / 100; return (step ? Math.ceil(c / step) * step : Math.ceil((c - 95) / 100) * 100 + 95) / 100; };
    return Object.freeze({
      on: true,
      unit: unit / 100,
      up,
      upTo: (x, ceiling) => { const shown = up(x); const most = Number(ceiling); return Number.isFinite(most) && most < shown ? Math.max(Number(x), most) : shown; },
      down: x => { const c = toCents(x); return c > 0 ? Math.floor(c / unit) * unit / 100 : c / 100; },
      near: x => Math.round(toCents(x) / unit) * unit / 100,
      fits: x => fits(toCents(x)),
    });
  };
  const key = line => `${line.id}:${line.selling_plan || ''}`;
  const existingLines = cart => {
    const grouped = new Map();
    for (const item of cart.items || []) {
      const line = {id:item.variant_id,quantity:item.quantity,selling_plan:item.selling_plan_allocation?.selling_plan?.id};
      const previous = grouped.get(key(line));
      if (previous) previous.quantity += line.quantity;
      else grouped.set(key(line), line);
    }
    return [...grouped.values()];
  };
  // Preserve existing quantities and selling plans. A bundle supplies missing
  // editions, never resets a buyer's cart or silently switches a subscription.
  const merge = (existing, desired) => {
    const lines = existing.map(i => ({...i}));
    for (const item of desired) {
      const match = lines.find(i => key(i) === key(item));
      if (!match) lines.push({...item});
      else match.quantity = Math.max(match.quantity,item.quantity);
    }
    return lines;
  };
  const cart = async () => {
    const r = await fetch(`${root}cart.js`,{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(12000)});
    if (!r.ok) throw new Error('Your cart could not be checked. Please try again.');
    return r.json();
  };
  const quote = (lines,country,currency,codes=[]) => {
    const cacheKey = JSON.stringify([lines,country,currency,codes]);
    const saved = cache.get(cacheKey);
    if (saved && Date.now()-saved.at < 30000) return saved.promise;
    const promise = (async () => {
      if (!lines.length || lines.length > 100) throw new Error('Check the offer in your cart.');
      const input = {buyerIdentity:{countryCode:country},lines:lines.map(i=>({merchandiseId:`gid://shopify/ProductVariant/${i.id}`,quantity:i.quantity,...(i.selling_plan?{sellingPlanId:`gid://shopify/SellingPlan/${i.selling_plan}`}:{})})),discountCodes:codes};
      const r = await askShopify(JSON.stringify({query,variables:{input}}));
      if (!r.ok) throw new Error('Offer preview unavailable.');
      let json;
      try { json = JSON.parse(r.text); } catch (error) { throw new Error('Offer preview unavailable.'); }
      const payload = json.data?.cartCreate;
      if (json.errors?.length || payload?.userErrors?.length || !payload?.cart) throw new Error('Offer preview unavailable.');
      const result = payload.cart;
      if (result.cost.subtotalAmount.currencyCode !== currency) throw new Error('Check the offer in your local currency in cart.');
      const nodes = result.lines.nodes;
      // Inventory adjustments must not produce a quote for fewer items than requested.
      const requested = lines.reduce((n,i)=>n+i.quantity,0);
      if (nodes.reduce((n,i)=>n+i.quantity,0) !== requested) throw new Error('Some items are unavailable. Please check your selection.');
      const original = nodes.reduce((n,i)=>n+Number(i.cost.subtotalAmount.amount),0);
      const total = Math.max(0, Number(result.cost.subtotalAmount.amount) - (result.discountAllocations || []).reduce((sum, allocation) => sum + Number(allocation.discountedAmount.amount), 0));
      if (!Number.isFinite(total) || !Number.isFinite(original)) throw new Error('Offer preview unavailable.');
      const freeQuantity = nodes.reduce((n,i)=>n+(Number(i.cost.subtotalAmount.amount)>0 && Number(i.cost.totalAmount?.amount)===0 ? i.quantity : 0),0);
      const quoted = nodes.map(i=>({id:Number(String(i.merchandise?.id||'').split('/').pop()),quantity:i.quantity,subtotal:Number(i.cost.subtotalAmount.amount),total:Number(i.cost.totalAmount?.amount)}));
      // The single prices in this answer (one-time lines only: a subscription plan may adjust its own price).
      const planned = new Set(lines.filter(i=>i.selling_plan).map(i=>Number(i.id)));
      const units = quoted.filter(i=>!planned.has(i.id) && i.quantity>0 && i.subtotal>0).map(i=>i.subtotal/i.quantity);
      return {original,total,saving:Math.max(0,original-total),freeQuantity,currency,lines:quoted,rounding:rounding(currency,units)};
    })();
    cache.set(cacheKey,{at:Date.now(),promise});
    promise.catch(()=>cache.delete(cacheKey));
    return promise;
  };
  async function preview(el) {
    const desired = JSON.parse(el.querySelector('[data-offer-lines]').textContent);
    const price = el.querySelector('[data-offer-price]');
    const saving = el.querySelector('[data-offer-saving]');
    const beforePrice = el.querySelector('[data-offer-before]');
    const context = el.querySelector('[data-offer-context]');
    const add = el.querySelector('[data-offer-add]');
    const status = el.querySelector('[data-offer-status]');
    const includeCart = el.dataset.includeCart === 'true';
    const show = moneyLike(el.dataset.moneySample) || money;
    // Codes an offer carries (data-offer-codes, e.g. the free first month with the whole archive): quoted with it,
    // and put on the cart before a membership is added with it, its line marked as the cart's complimentary month.
    const offerCodes = (el.dataset.offerCodes || '').split(',').map(c=>c.trim()).filter(Boolean);
    const retry = el.querySelector('[data-offer-retry]');
    if (!el._offerFallback) el._offerFallback = {price:price.innerHTML,context:context?.textContent || '',saving:saving.textContent};
    if (retry) {
      retry.hidden=true;
      retry.onclick=()=>preview(el);
    }
    saving.textContent=el._offerFallback.saving;
    el.dataset.offerState='loading';
    if (!el._offerAddBound) {
    el._offerAddBound=true;
    add.addEventListener('click',async()=>{
      if (add.disabled) return;
      add.disabled=true;add.textContent='Adding…';status.textContent='';
      try {
        const current=await cart();
        const existing=existingLines(current);
        const items=desired.map(i=>({...i,quantity:Math.max(0,i.quantity-(existing.find(x=>key(x)===key(i))?.quantity||0))})).filter(i=>i.quantity>0);
        const offer = desired.some(i => i.selling_plan) ? (desired.length > 1 ? 'avalon_membership_archive' : 'avalon_membership') : 'avalon_complete_archive';
        items.forEach(item => { item.properties = {...item.properties, ...window.ViuletUpsell?.properties(offer, el, includeCart || el.closest('.cart-drawer-overlay') ? 'upsell' : 'primary')}; });
        if(offerCodes.length && items.some(i=>i.selling_plan)){
          const have=(current.discount_codes||[]).map(c=>c.code).filter(Boolean);
          const wanted=[...have,...offerCodes.filter(c=>!have.some(h=>h.toLowerCase()===c.toLowerCase()))];
          if(wanted.length!==have.length){
            const u=await fetch(`${root}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({discount:wanted.join(',')})});
            if(!u.ok) throw new Error('Please check your selection in cart.');
          }
          items.forEach(item=>{ if(item.selling_plan) item.properties={...item.properties,_avalon_complimentary:'v1'}; });
        }
        if(items.length){
          const r=await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items})});
          const response=await r.json();
          if(!r.ok) throw new Error(response.description||'Please check your selection in cart.');
        }
        window.location.assign(`${root}cart`);
      }catch(error){
        // A timed-out response might have added items: a new click checks cart first.
        status.textContent=error.message||'Please check your cart and try again.';
        add.disabled=false;add.textContent='Check and add to cart';
      }
    });
    }
    try {
      const current = includeCart ? await cart() : {items:[]};
      const lines = includeCart ? merge(existingLines(current),desired) : desired;
      if (includeCart && lines.length === existingLines(current).length && lines.every(i=>existingLines(current).some(x=>key(x)===key(i)&&x.quantity===i.quantity))) {
        add.disabled=true;add.textContent='Already in your cart';
      }
      const codes = [...new Set([...(current.discount_codes || []).map(c=>c.code).filter(Boolean), ...offerCodes])];
      const withRetry = async requested => {
        for(let attempt=0;attempt<2;attempt++){
          try{return await quote(requested,el.dataset.country,el.dataset.currency,codes);}
          catch(error){if(attempt===1)throw error;await new Promise(resolve=>setTimeout(resolve,750));}
        }
      };
      const [q, baseline] = await Promise.all([
        withRetry(lines),
        el.dataset.offerDelta==='true' && current.items.length ? withRetry(existingLines(current)) : Promise.resolve({total:0,original:0})
      ]);
      price.hidden=false;
      // Rounded as the currency's single prices are (see rounding above): what is paid up, what is saved down.
      const r = q.rounding || exact;
      const say = (amount, currency) => { const text = show(amount, currency); return r.on ? text.replace(/[.,]00(?=\D*$)/, '') : text; };
      if(el.dataset.offerDelta==='true') {
        if (Math.abs(baseline.total - Number(current.total_price || 0) / 100) > 0.02) throw new Error('Cart-specific price cannot be reproduced.');
        const change = Math.round((q.total - baseline.total) * 100) / 100;
        const regular = Math.max(0, q.original - baseline.original);
        const extra = change > 0 ? r.upTo(change, regular) : change < 0 ? -r.down(-change) : 0;
        const saved = r.down(Math.max(0, regular - extra));
        const count = lines.reduce((sum, line) => sum + line.quantity, 0) - current.items.reduce((sum, line) => sum + line.quantity, 0);
        if(beforePrice) {
          beforePrice.textContent = say(regular, q.currency);
          beforePrice.hidden = saved < 0.01;
        }
        price.textContent = extra < 0 ? `− ${say(-extra,q.currency)}` : extra > 0 ? `+ ${say(extra,q.currency)}` : say(0,q.currency);
        if(context) context.textContent = `${extra < 0 ? el.dataset.reductionLabel : extra > 0 ? el.dataset.extraLabel : el.dataset.includedLabel} · ${(el.dataset.missingLabel || '').replace('__COUNT__',count)}`;
        saving.textContent = saved > 0 ? (el.dataset.saveLabel || '').replace('__AMOUNT__',say(saved,q.currency)) : '';
        if(extra < 0) saving.textContent += ` ${el.dataset.existingLabel || ''}`;
      } else {
        const total = r.upTo(q.total, q.original);
        const saved = r.down(Math.max(0, q.original - total));
        price.textContent=`${say(total,q.currency)} ${el.dataset.priceOnly==='true'?'':includeCart?'cart subtotal':'today'}`;
        saving.textContent=saved>0?`${q.freeQuantity ? `${q.freeQuantity} ${q.freeQuantity===1?'item':'items'} complimentary · ` : ''}Save ${say(saved,q.currency)}.`:'The current price of your selection.';
      }
      el.dataset.offerState='ready';
    } catch (error) {
      el.dataset.offerState='unavailable';
      if(el.dataset.offerDelta==='true'){
        price.innerHTML=el._offerFallback.price;
        price.hidden=false;
        if(beforePrice)beforePrice.hidden=true;
        if(context)context.textContent=el._offerFallback.context;
        saving.textContent=el.dataset.errorLabel || 'Your archive offer will be calculated in cart.';
        if(retry)retry.hidden=false;
      }
      else {saving.textContent='Your eligible offer will be calculated in cart.';if(includeCart)price.hidden=true;}
    }

  }
  const init=()=>document.querySelectorAll('[data-avalon-live-offer]:not([data-offer-initialized])').forEach(el=>{
    el.dataset.offerInitialized='true';
    if(el.dataset.offerDelta==='true'){preview(el);return;}
    const observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){observer.disconnect();preview(el);}},{rootMargin:'150px'});
    observer.observe(el);
  });
  window.AvalonLivePricing={init,quote,merge,money,moneyLike,rounding};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
  document.addEventListener('shopify:section:load',init);
  const updates = new MutationObserver(records => { if(records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-avalon-live-offer]') || node.querySelector?.('[data-avalon-live-offer]'))))) init(); });
  updates.observe(document.documentElement,{childList:true,subtree:true});
})();
